import { FC, useCallback, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { ReactComponent as DownloadIcon } from 'assets/dowload_file_icon.svg';
import { ReactComponent as CloseIcon } from 'assets/close_icon.svg';
import { UploadInputTransFields, IUploadInputProps } from './types';
import { uploadInputTranslations } from './translations';
import styles from './uploadInput.module.scss';

const DEFAULT_ACCEPT = ['image/svg+xml', 'image/jpeg', 'image/png'];
const MB = 1024 * 1024;
// image/svg+xml → SVG, image/jpeg → JPG
const mimeToLabel = (mime: string) =>
  mime
    .replace(/^image\//, '')
    .replace(/\+xml$/, '')
    .replace('jpeg', 'jpg')
    .toUpperCase();

export const UploadInput: FC<IUploadInputProps> = ({
  name,
  path,
  isInstantUpload = false,
  label,
  required = false,
  validation,
  classNameProp,
  isRemoveMargin,
  disabled,
  accept = DEFAULT_ACCEPT,
  maxSizeMb,
}) => {
  const {
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<UploadInputTransFields>([uploadInputTranslations.name]);

  const formContext = useFormContext();

  const {
    setValue,
    formState: { errors },
  } = formContext;

  const { uploadImage } = useS3ImageUploader<Record<string, string>>({ setValue });

  // The blob URL behind the current preview; freed when replaced or on unmount,
  // otherwise every picked file stays in memory for the life of the tab.
  const previewUrlRef = useRef<string>();
  const setPreview = useCallback((url: string) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url || undefined;
  }, []);
  useEffect(() => () => setPreview(''), [setPreview]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles[0]) {
        const file = acceptedFiles[0];
        if (!accept.includes(file.type)) {
          showAlert('Incorrect file type', 'warning');
          return;
        }
        if (maxSizeMb && file.size > maxSizeMb * MB) {
          showAlert(`File is larger than ${maxSizeMb} MB`, 'warning');
          return;
        }
        if (isInstantUpload) {
          try {
            await uploadImage(file, name, path);
            showAlert('Image was upload', 'success');
          } catch {
            showAlert('Problems with image upload', 'warning');
          }
        } else {
          const preview = URL.createObjectURL(file);
          setPreview(preview);
          setValue(name, preview, { shouldDirty: true });
          setValue(`${name}File`, file, { shouldDirty: true });
        }
      }
    },
    [name, path, accept, maxSizeMb, setPreview]
  );

  const { getRootProps, getInputProps, open } = useDropzone({ onDrop, noClick: true });

  const getContent = useCallback(
    (value: string | undefined | null) => {
      if (value) {
        return (
          <div className={`${styles.imageBox} ${styles.selectedFile}`}>
            <img src={value} alt='point image' className={styles.image} />
            {!disabled && (
              <button
                className={styles.deleteBtn}
                type='button'
                onClick={() => {
                  setPreview('');
                  setValue(name, '', { shouldDirty: true });
                }}
                disabled={disabled}>
                <CloseIcon />
              </button>
            )}
          </div>
        );
      } else if (!disabled) {
        return (
          <div className={`${styles.imageBox} ${styles.selectFile} ${styles.noFile} ${errors[name] && styles.error}`}>
            <DownloadIcon />
            <p className={styles.selectText}>{t('firstRow')}</p>
            <p className={styles.selectText}>
              <span>{t('secondRow')}&nbsp; </span>
              <button type='button' onClick={open} className={styles.selectBtn} disabled={disabled}>
                {t('btnText')}
              </button>
            </p>
            <p className={styles.supportedText}>
              {t('lastRow')}: {accept.map(mimeToLabel).join(', ')}
              {maxSizeMb ? `, ≤ ${maxSizeMb} MB` : ''}
            </p>
          </div>
        );
      } else {
        return (
          <div className={`${styles.imageBox} ${styles.selectFile} ${styles.noFile}`}>
            <p className={styles.selectText}>{t('noImageRow')}</p>
          </div>
        );
      }
    },
    [disabled, errors, name, accept, maxSizeMb, setPreview]
  );

  return (
    <div className={styles.inputWrapper} style={isRemoveMargin ? { marginBottom: 0 } : {}} {...getRootProps()}>
      {label && <p className={styles.labelText}>{label}</p>}
      <Controller
        control={formContext?.control}
        name={name}
        rules={{ required: required, validate: validation?.validate }}
        render={({ field: { value } }) => (
          <>
            <input
              {...getInputProps()}
              className={`${styles.input} ${classNameProp}`}
              type='file'
              id={name}
              accept={accept.join(', ')}
              required={required}
              disabled={disabled}
            />
            {getContent(value)}
          </>
        )}
      />
    </div>
  );
};
